import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Link,
  TextField,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { useParams } from "react-router-dom";

import { SubscriptionSeatTypes } from "@safetyculture/s12-apis-connect-web/s12/common/seat_types_pb";
import {
  GetOrgWithSubscriptionResponse,
  SubscriptionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { UrlParamTypes } from "../../../../types/org-display";
import EnumDropdown from "../subscription-actions/enum-dropdown";
import {
  getOrgWithSubscription,
  getOrganizationIdentity,
} from "api/endpoints/organisation";
import { getSubscriptionSeatUsage } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import { subscriptionSeatTypeDisplayMapping } from "constants/component";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { mapRegionStringToRegionCode } from "helpers/env-values-helper";
import { validateS12ID } from "helpers/s12-helpers";

interface TransferUserProps {
  userFirstName: string;
  userLastName: string;
  handleAssigneeId: (assigneeId: string) => void;
  handleTargetOrgId: (targetOrgId: string) => void;
  handleTargetSeatType: (targetSeatType: SubscriptionSeatTypes) => void;
  handleTransferUserBtn: (isDisabled: boolean) => void;
  sourceOrgId: string;
  seatType: SubscriptionSeatTypes;
}

const TransferUser = ({
  userFirstName,
  userLastName,
  handleTargetOrgId,
  handleTargetSeatType,
  handleTransferUserBtn,
  sourceOrgId,
  seatType,
}: TransferUserProps) => {
  const { regionCode } = useParams<UrlParamTypes>();
  const [targetOrgId, setTargetOrgId] = useState("");
  const [targetSubId, setTargetSubId] = useState("");
  const [targetSeatType, setTargetSeatType] = useState(seatType);
  const [targetOrgData, setTargetOrgData] =
    useState<GetOrgWithSubscriptionResponse>();
  const [isTargetPaidOrg, setIsTargetPaidOrg] = useState(false);
  const [getOrgError, setGetOrgError] = useState<GrpcError>();
  const [getSeatUsageError, setGetSeatUsageError] = useState<GrpcError>();
  const [unusedSeat, setUnusedSeat] = useState(-1);
  const [checkList, setCheckList] = useState([false, false, false, false]);
  const [targetOrgDataCenter, setTargetOrgDataCenter] = useState<string>("");
  const [regionErrorMessage, setRegionErrorMessage] = useState("");

  const handleCheckboxChange = (index: number, isChecked: boolean) => {
    const newCheckboxes = [...checkList];
    newCheckboxes[index] = isChecked;
    setCheckList(newCheckboxes);
  };

  const { showError } = useContext(SnackbarContext);

  const getTargetOrgData = useCallback(
    async (orgId: string) => {
      if (orgId.length == 0 || orgId == sourceOrgId) return Promise.resolve();

      setTargetOrgData(undefined);
      setGetOrgError(undefined);

      getOrgWithSubscription(
        {
          roleId: orgId,
        },
        callOptionsHeaders(regionCode, sourceOrgId)
      )
        .then((orgDataResp) => {
          setTargetOrgData(orgDataResp);
          setTargetSubId(orgDataResp?.role?.subscription[0].id || "");
          setIsTargetPaidOrg(
            (orgDataResp?.role?.subscription[0]?.type ??
              SubscriptionType.FREE_ORG) !== SubscriptionType.FREE_ORG
          );
        })
        .catch((e: GrpcError) => {
          showError(e);
          setGetOrgError(e);
          setIsTargetPaidOrg(false);
          setTargetSubId("");
          setUnusedSeat(-1);
        });
    },
    [regionCode, sourceOrgId, showError]
  );

  const getTargetOrgIdLabel = () => {
    if (targetOrgData?.role) {
      if (isTargetPaidOrg) {
        return `Target Org Id (${targetOrgData.role.name})`;
      } else {
        return "Target Org Id (must be a paid org)";
      }
    } else {
      return "Target Org Id";
    }
  };

  const getTargetOrgName = () => {
    if (targetOrgData?.role) {
      if (isTargetPaidOrg) {
        return targetOrgData.role.name;
      } else {
        return "No Valid Org Specified (must be a paid org)";
      }
    } else {
      return "No Valid Org Specified";
    }
  };

  useEffect(() => {
    const fetchSeatUsage = (subscriptionId: string, orgId: string) => {
      if (
        orgId.length == 0 ||
        orgId == sourceOrgId ||
        subscriptionId.length == 0
      )
        return Promise.resolve();
      setUnusedSeat(-1);
      getSubscriptionSeatUsage(
        {
          subscriptionId,
          orgId,
        },
        callOptionsHeaders(regionCode, targetOrgId)
      )
        .then((seatUsageResp) => {
          const getUnusedSeatType = (targetSeatType: SubscriptionSeatTypes) => {
            let seat = 0;
            switch (targetSeatType) {
              case SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_PREMIUM:
                seat = seatUsageResp?.premiumSeatUsageSummary?.unusedSeats || 0;
                break;
              case SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_COLLABORATOR:
                seat =
                  seatUsageResp?.collaboratorSeatUsageSummary?.unusedSeats || 0;
                break;
              case SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_LITE:
                seat = seatUsageResp?.liteSeatUsageSummary?.unusedSeats || 0;
                break;
            }
            setUnusedSeat(seat);
          };

          getUnusedSeatType(targetSeatType);
        })
        .catch((e: GrpcError) => {
          setGetSeatUsageError(e);
          showError(e);
        });
    };

    handleTargetSeatType(targetSeatType);
    fetchSeatUsage(targetSubId, targetOrgId);

    // eslint-disable-next-line
  }, [targetSeatType, targetSubId]);

  useEffect(() => {
    handleTargetOrgId(targetOrgId);
    getTargetOrgData(targetOrgId);
  }, [targetOrgId, handleTargetOrgId, getTargetOrgData]);

  useEffect(() => {
    if (!validateS12ID(targetOrgId)) {
      return;
    }

    const headers = callOptionsHeaders(regionCode, targetOrgId);

    const handleRegionComparison = (identityRegion: string) => {
      setTargetOrgDataCenter(identityRegion);
      const targetRegionCode = mapRegionStringToRegionCode(identityRegion);
      if (targetRegionCode !== regionCode) {
        setRegionErrorMessage(
          `Target Org is in a different data center (${targetRegionCode}) from the source org (${regionCode}).`
        );
      } else {
        setRegionErrorMessage("");
      }
    };

    getOrganizationIdentity({ orgId: targetOrgId }, headers)
      .then((identity) => {
        handleRegionComparison(identity.region);
      })
      .catch((error) => {
        setRegionErrorMessage(
          `Cannot get organisation data due to ${error.message}`
        );
        showError(error);
      });
  }, [targetOrgId, regionCode, showError]);

  useEffect(() => {
    const hasSeat = unusedSeat > 0;
    handleTransferUserBtn(
      !isTargetPaidOrg || !hasSeat || !checkList.every((isChecked) => isChecked)
    );
  }, [
    handleTransferUserBtn,
    isTargetPaidOrg,
    targetOrgId,
    targetSeatType,
    unusedSeat,
    checkList,
  ]);

  const renderTargetOrgAlert = () => {
    if (getOrgError != undefined) {
      return (
        <Grid2 size={12}>
          {targetOrgDataCenter !== regionCode ? (
            <Alert
              severity="warning"
              variant="filled"
              style={{ margin: "2px" }}
            >
              `Target Org is in a different data center: (${targetOrgDataCenter}
              )`
            </Alert>
          ) : (
            <Alert
              severity="warning"
              variant="filled"
              style={{ margin: "2px" }}
            >
              Cannot get organisation data due to <b>{getOrgError.message}</b>
            </Alert>
          )}
        </Grid2>
      );
    } else if (
      !isTargetPaidOrg &&
      targetOrgId.length > 0 &&
      targetOrgData?.role
    ) {
      return (
        <Grid2 size={12}>
          <Alert severity="warning" variant="filled" style={{ margin: "2px" }}>
            {getTargetOrgName()}
          </Alert>
        </Grid2>
      );
    } else if (regionErrorMessage.length > 0) {
      return (
        <Grid2 size={12}>
          <Alert severity="warning" variant="filled" style={{ margin: "2px" }}>
            {regionErrorMessage}
          </Alert>
        </Grid2>
      );
    }
    return null;
  };

  const renderSeatAlert = () => {
    if (getSeatUsageError != undefined) {
      return (
        <Grid2 size={12}>
          <Alert severity="warning" variant="filled" style={{ margin: "2px" }}>
            Cannot get seat usage data at the target organisation due to{" "}
            <b>{getSeatUsageError.message}</b>
          </Alert>
        </Grid2>
      );
    } else if (
      targetSeatType !=
        SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED &&
      isTargetPaidOrg &&
      unusedSeat === 0 &&
      getOrgError === undefined
    ) {
      return (
        <Grid2 size={12}>
          <Alert severity="warning" variant="filled" style={{ margin: "2px" }}>
            Not enough{" "}
            <b>{subscriptionSeatTypeDisplayMapping[targetSeatType]}</b> at the
            target org.
          </Alert>
        </Grid2>
      );
    }

    return null;
  };

  return (
    <Grid2
      container
      direction="row"
      spacing={1}
      style={{ minWidth: 350 }}
      marginTop={1}
    >
      <Grid2 size={12}>
        <Alert severity="info" variant="filled" style={{ margin: "2px" }}>
          <div>
            <div style={{ marginBottom: "8px" }}>
              You are about to transfer{" "}
              <b>
                {userFirstName} {userLastName}
              </b>{" "}
              from this organisation with their templates and inspections to{" "}
              <b>{getTargetOrgName()}</b>. This action will create a{" "}
              <b>new user document</b> and set the user&apos;s preferred login
              to the target org, then <b>deactivate</b> the existing user
              document in the current org.
            </div>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              <li>
                Contact #business-systems slack channel if {userFirstName}
                &apos;s inspection documents contain <b>GRS and/or sites</b>,
                because <b>they will be broken</b> after the transfer.
              </li>
              <li>
                <b>
                  You will need to ensure {`${userFirstName} ${userLastName}'s`}{" "}
                  templates and inspections have correct access rules after the
                  transfer, as access rules are reset to just the owner when
                  documents are transferred to another organisation.
                </b>
              </li>
            </ul>
          </div>
        </Alert>
        <FormGroup style={{ marginTop: "16px" }}>
          <FormControlLabel
            control={
              <Checkbox
                onChange={(e) => {
                  handleCheckboxChange(0, e.target.checked);
                }}
                checked={checkList[0]}
                data-testid="read-collatoral-checkbox"
                style={{ padding: "0px 9px 0px 0px" }}
              />
            }
            label={
              <div>
                <span>I have read the </span>
                <Link
                  href={"https://safetyculture.atlassian.net/wiki/x/KwCLqw"}
                  target="_blank"
                >
                  Org Split/Merge collateral{" "}
                </Link>
                <span>
                  {" "}
                  and set expectations with the customer regarding data that
                  will not follow users to their new organisation
                </span>
              </div>
            }
            style={{ alignItems: "flex-start", margin: 0 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                onChange={(e) => {
                  handleCheckboxChange(1, e.target.checked);
                }}
                checked={checkList[1]}
                data-testid="user-sync-checkbox"
              />
            }
            label="I have informed users to sync their mobile apps prior to the transfer."
          />
          <FormControlLabel
            control={
              <Checkbox
                onChange={(e) => {
                  handleCheckboxChange(2, e.target.checked);
                }}
                checked={checkList[2]}
                data-testid="user-logout-checkbox"
              />
            }
            label="I have informed users to log out of their web and mobile apps prior to the transfer."
          />
          <FormControlLabel
            control={
              <Checkbox
                onChange={(e) => {
                  handleCheckboxChange(3, e.target.checked);
                }}
                checked={checkList[3]}
                data-testid="user-organisation-checkbox"
              />
            }
            label="I have informed users to check that they are logged into the correct organisation after the user transfer."
          />
        </FormGroup>
      </Grid2>
      {renderTargetOrgAlert()}
      {renderSeatAlert()}
      <Grid2 size={12} marginTop={2}>
        <TextField
          required
          id="targetOrgId-input"
          label={getTargetOrgIdLabel()}
          placeholder="Target Org Id"
          value={targetOrgId}
          onChange={(event) => {
            setTargetOrgId(event.target.value.trim());
          }}
          variant="outlined"
          size="small"
          fullWidth
          inputProps={{
            "data-anchor": "targetOrgId-input",
            "data-testid": "targetOrgId-input",
          }}
        />
      </Grid2>
      <Grid2 size={12} marginTop={1}>
        <EnumDropdown
          enum={SubscriptionSeatTypes}
          label="Target Seat Type*"
          defaultValue={targetSeatType.toString()}
          onValueChange={(value: string) => {
            setTargetSeatType(Number(value) as SubscriptionSeatTypes);
          }}
          displayMapping={subscriptionSeatTypeDisplayMapping}
        />
      </Grid2>
    </Grid2>
  );
};

export default TransferUser;
